import { z } from 'zod'

// Helper function to format phone number to E.164 format
export const formatToE164 = (phone: string): string => {
  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '')

  // If the number already has a plus, we need to handle it specially
  if (phone.startsWith('+')) {
    // If it's already in E.164 format with +1, return as is
    if (phone.startsWith('+1') && digits.length === 11) {
      return phone
    }

    // Otherwise, just use the digits
    if (digits.length === 10) {
      return `+1${digits}`
    }

    if (digits.length === 11 && digits.startsWith('1')) {
      return `+${digits}`
    }
  }

  // For US phone numbers (10 digits)
  if (digits.length === 10) {
    return `+1${digits}`
  }

  // If number starts with 1 and has 11 digits (US with country code)
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+${digits}`
  }

  // If we've gotten here and the number doesn't match expected patterns,
  // Still make a best effort to format it for Cognito
  return `+1${digits.slice(-10)}`
}

export const phoneSchemaBase = z
  .string()
  .min(10, 'Phone number must be at least 10 digits')
  .refine(
    (val) => {
      const digits = val.replace(/\D/g, '')
      // Ensure we have at least 10 digits
      return digits.length >= 10
    },
    {
      message: 'Please enter a valid US phone number',
    },
  )

// Shared phone number validation for US numbers
export const phoneSchema = phoneSchemaBase.transform(formatToE164) // Transform to E.164 format

// Consumer sign-up form schema
export const consumerSignupSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Please enter a valid email address'),
  phoneNumber: phoneSchema,
})

export type ConsumerSignupFormData = z.infer<typeof consumerSignupSchema>

// Confirmation code form schema
export const confirmationCodeSchema = z.object({
  code: z.string().min(6, 'Please enter the 6-digit code').max(6, 'Please enter the 6-digit code'),
})

export type ConfirmationCodeFormData = z.infer<typeof confirmationCodeSchema>

// Consumer login email form schema
export const consumerLoginEmailSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
})

export type ConsumerLoginEmailFormData = z.infer<typeof consumerLoginEmailSchema>

// Consumer login phone number form schema
export const consumerLoginPhoneNumberSchema = z.object({
  phoneNumber: phoneSchemaBase,
})

export type ConsumerLoginPhoneNumberFormData = z.infer<typeof consumerLoginPhoneNumberSchema>

// Consumer login OTP form schema
export const consumerLoginOtpSchema = z.object({
  otp: z.string().min(6, 'Please enter the 6-digit OTP code').max(6, 'Please enter the 6-digit OTP code'),
})

export type ConsumerLoginOtpFormData = z.infer<typeof consumerLoginOtpSchema>
